import React, { createContext, useContext, useMemo } from 'react';
import { AnalyticsExportContext } from '../utils/analyticsTableExport';

/**
 * SHER-905. Where an exported analytics file gets its NAME from.
 *
 * The five consumer tickets (SHER-306, SHER-560, SHER-585 iii, SHER-807, SHER-976) each add a
 * table on one of these tabs, and each wants its download named after the filters in force.
 * Handing every table a `context` prop to assemble would have meant five copies of the same
 * two conversions, and the conversions are exactly the kind that drift:
 *
 *   - the active range arrives as MILLISECOND TIMESTAMPS (`AnalyticsBeta/index.tsx` sets them
 *     from `dates[0].getTime()`, and the props are declared `string` while holding numbers),
 *     so each table would invent its own ms -> day formatting;
 *   - `departmentSelected` is a department UUID, not the label a filename wants, so each table
 *     would write its own lookup against `departments` — or, more likely, ship the raw uuid.
 *
 * Five tables doing that independently is five filenames that disagree, which is the precise
 * failure an enabler exists to prevent. So the derivation happens ONCE, here, and a table that
 * wants an export just renders the button.
 *
 * The default is an empty context rather than a throw: a table rendered outside this provider
 * (a Storybook frame, a unit test, a future tab) still exports, just under a bare filename.
 */
const ExportContextChannel = createContext<AnalyticsExportContext>({});

/** Read the filters in force. Returns `{}` outside a provider — see the note above. */
export function useAnalyticsExportContext(): AnalyticsExportContext {
  return useContext(ExportContextChannel);
}

interface DepartmentOption {
  uuid?: string;
  name: string;
}

interface AnalyticsExportContextProviderProps {
  /** Active range, as the millisecond timestamps the page actually holds. */
  startDate: number | null;
  endDate: number | null;
  /** The selected department's UUID, or the sentinel "all" when unscoped. */
  departmentSelected: string | null;
  departments: DepartmentOption[] | null;
  children: React.ReactNode;
}

/**
 * Format a timestamp as an ISO calendar day.
 *
 * Deliberately NOT `toISOString().slice(0, 10)`: that converts to UTC first, so a range a
 * clinician picked as 1 September in Douala (UTC+1) would be named 31 August. The date picker
 * deals in local days, so the filename does too.
 */
function toIsoDay(timestamp: number | null): string | undefined {
  if (timestamp === null || timestamp === undefined || Number.isNaN(timestamp)) {
    return undefined;
  }
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) {
    return undefined;
  }
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

export function AnalyticsExportContextProvider({
  startDate,
  endDate,
  departmentSelected,
  departments,
  children,
}: AnalyticsExportContextProviderProps) {
  const value = useMemo<AnalyticsExportContext>(() => {
    // "all" is the selector's sentinel for no department filter, not a department. Naming it
    // in the file would claim a scope the export does not have.
    const department =
      departmentSelected && departmentSelected !== 'all'
        ? departments?.find((candidate) => candidate.uuid === departmentSelected)
        : undefined;

    return {
      from: toIsoDay(startDate),
      to: toIsoDay(endDate),
      service: department?.name,
    };
  }, [startDate, endDate, departmentSelected, departments]);

  return <ExportContextChannel.Provider value={value}>{children}</ExportContextChannel.Provider>;
}

export default AnalyticsExportContextProvider;
